/**
 * FormCommandHandler - AI Command Handler for Form Components
 *
 * Handles:
 * - Form property modifications
 * - Workflow binding commands
 * - Form mode settings
 */

import { Component, ComponentType } from "@/types";
import {
  BaseCommandHandler,
  CommandResult,
  PropertyChange,
} from "../BaseCommandHandler";

/**
 * FormCommandHandler Class
 *
 * Handles AI commands for FORM_CONTAINER components (property modifications only).
 * Form creation is now handled by the visual editor agent pipeline.
 */
export class FormCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Workflow binding patterns
    this.commandPatterns.set(
      /(?:set |bind |connect )?workflow (?:to )?["']?([^"']+)["']?/i,
      (match) => ({ properties: { workflowId: match[1] } })
    );

    this.commandPatterns.set(
      /(?:set |bind )?(?:to )?workflow (?:id )?["']?([a-f0-9]{24})["']?/i,
      (match) => ({ properties: { workflowId: match[1] } })
    );

    // Form mode patterns
    this.commandPatterns.set(
      /(?:set |make )?(?:form )?mode (?:to )?(create|edit)/i,
      (match) => ({ properties: { mode: match[1].toLowerCase() } })
    );

    this.commandPatterns.set(
      /(?:make )?(?:form )?(?:for )?(creating|editing)/i,
      (match) => ({
        properties: { mode: match[1] === "creating" ? "create" : "edit" },
      })
    );

    // Validation behavior patterns
    this.commandPatterns.set(
      /validate on (blur|change|submit)/i,
      (match) => {
        const setting = match[1].toLowerCase();
        return {
          properties: {
            validateOnBlur: setting === "blur",
            validateOnChange: setting === "change",
          },
        };
      }
    );

    this.commandPatterns.set(
      /reset (?:form )?(?:on|after) submit/i,
      () => ({ properties: { resetOnSubmit: true } })
    );

    this.commandPatterns.set(
      /(?:don't |do not )?reset (?:form )?(?:on|after) submit/i,
      () => ({ properties: { resetOnSubmit: false } })
    );

    // Layout patterns (inherited from FlexContainer concepts)
    this.commandPatterns.set(
      /(?:set |make )?direction (?:to )?(row|column)/i,
      (match) => ({ properties: { flexDirection: match[1].toLowerCase() } })
    );

    this.commandPatterns.set(
      /(?:set |make )?(?:form )?(vertical|horizontal)/i,
      (match) => ({
        properties: {
          flexDirection: match[1] === "vertical" ? "column" : "row",
        },
      })
    );

    this.commandPatterns.set(
      /(?:set )?gap (?:to )?(\d+)(?:px)?/i,
      (match) => ({ properties: { gap: parseInt(match[1], 10) } })
    );

    this.commandPatterns.set(
      /(?:set )?padding (?:to )?(\d+)(?:px)?/i,
      (match) => ({ properties: { padding: parseInt(match[1], 10) } })
    );

    // Label patterns
    this.commandPatterns.set(
      /(?:set |change )?(?:form )?label (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { label: match[1] } })
    );

    // Schema binding
    this.commandPatterns.set(
      /(?:bind |connect |use )?schema ["']?([^"']+)["']?/i,
      (match) => ({ properties: { schemaId: match[1] } })
    );
  }

  /**
   * Check if this handler can process the command
   */
  canHandle(command: string, component: Component): boolean {
    return (
      component.type === ComponentType.FORM_CONTAINER &&
      this.hasFormKeywords(command)
    );
  }

  /**
   * Check for form-related keywords
   */
  private hasFormKeywords(command: string): boolean {
    const keywords = [
      "form",
      "workflow",
      "submit",
      "validate",
      "validation",
      "reset",
      "mode",
      "create",
      "edit",
      "schema",
      "gap",
      "padding",
      "direction",
      "vertical",
      "horizontal",
      "label",
    ];
    const lowerCommand = command.toLowerCase();
    return keywords.some((keyword) => lowerCommand.includes(keyword));
  }

  /**
   * Parse and execute the command
   */
  async parseCommand(
    command: string,
    component: Component
  ): Promise<CommandResult> {
    return this.handlePropertyCommands(command, component);
  }

  /**
   * Handle property modification commands
   */
  private async handlePropertyCommands(
    command: string,
    component: Component
  ): Promise<CommandResult> {
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {}, style: {} };

    for (const part of commandParts) {
      const patternMatch = this.matchPatterns(part, component);
      if (patternMatch) {
        if (patternMatch.properties) {
          changes.properties = { ...changes.properties, ...patternMatch.properties };
        }
        if (patternMatch.style) {
          changes.style = { ...changes.style, ...patternMatch.style };
        }
      }

      // Parse colors
      const colorValue = this.parseColorValue(part);
      if (colorValue) {
        if (part.toLowerCase().includes("background") || part.toLowerCase().includes("bg")) {
          changes.properties = { ...changes.properties, backgroundColor: colorValue };
        }
      }
    }

    if (
      Object.keys(changes.properties || {}).length > 0 ||
      Object.keys(changes.style || {}).length > 0
    ) {
      const validation = this.validateChanges(changes, component);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          suggestions: await this.getSuggestions(component),
        };
      }

      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component),
        },
        suggestions: await this.getSuggestions(component),
      };
    }

    return {
      success: false,
      error: "Could not understand the form command",
      suggestions: await this.getSuggestions(component),
    };
  }

  /**
   * Validate changes for form container
   */
  protected override validateChanges(
    changes: PropertyChange,
    component: Component
  ): { valid: boolean; error?: string } {
    // Validate mode
    if (changes.properties?.mode) {
      const validModes = ["create", "edit"];
      if (!validModes.includes(changes.properties.mode)) {
        return {
          valid: false,
          error: `Invalid mode: ${changes.properties.mode}. Use 'create' or 'edit'.`,
        };
      }
    }

    // Validate flexDirection
    if (changes.properties?.flexDirection) {
      const validDirections = ["row", "column"];
      if (!validDirections.includes(changes.properties.flexDirection)) {
        return {
          valid: false,
          error: `Invalid direction: ${changes.properties.flexDirection}. Use 'row' or 'column'.`,
        };
      }
    }

    // Validate gap/padding are positive numbers
    if (changes.properties?.gap !== undefined) {
      const gap = Number(changes.properties.gap);
      if (isNaN(gap) || gap < 0) {
        return { valid: false, error: "Gap must be a positive number" };
      }
    }

    if (changes.properties?.padding !== undefined) {
      const padding = Number(changes.properties.padding);
      if (isNaN(padding) || padding < 0) {
        return { valid: false, error: "Padding must be a positive number" };
      }
    }

    return super.validateChanges(changes, component);
  }

  /**
   * Get example commands
   */
  async getExampleCommands(): Promise<string[]> {
    return [
      'Set workflow to "user-registration"',
      "Set mode to edit",
      "Set mode to create",
      "Validate on blur",
      "Validate on change",
      "Reset form after submit",
      "Set gap to 20",
      "Set padding to 24",
      "Make form vertical",
      "Make form horizontal",
      'Set label to "Contact Form"',
      "Use schema user_profile",
    ];
  }

  /**
   * Get suggestions based on current state
   */
  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];

    // Check workflow binding
    if (!component.properties?.workflowId) {
      suggestions.push("Bind to workflow", "Set workflow ID");
    }

    // Mode suggestions
    if (component.properties?.mode === "create") {
      suggestions.push("Change to edit mode");
    } else {
      suggestions.push("Change to create mode");
    }

    // Validation suggestions
    if (!component.properties?.validateOnBlur) {
      suggestions.push("Enable validate on blur");
    }
    if (!component.properties?.validateOnChange) {
      suggestions.push("Enable validate on change");
    }

    // Layout suggestions
    if (component.properties?.flexDirection === "column") {
      suggestions.push("Make horizontal");
    } else {
      suggestions.push("Make vertical");
    }

    // Common adjustments
    suggestions.push(
      "Increase gap",
      "Decrease padding",
      'Change label to "..."'
    );

    return suggestions.slice(0, 10);
  }
}

// Export singleton instance
export const formCommandHandler = new FormCommandHandler();

export default formCommandHandler;
